<template>
  <div class="flex flex-col gap-2">
    <h1
      class="heading-md text-center"
      v-text="props.title"
    />
  </div>
  <form
    class="flex w-full flex-col gap-6"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />

    <BaseInlineNotification
      v-if="submissionError"
      heading-text="An Error Occurred"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="submissionError"
      />
    </BaseInlineNotification>

    <BaseTextInput
      id="email"
      v-model="values.email"
      autocomplete="email"
      label="Email"
      name="email"
      required
      type="email"
      validation-rules="required|email"
    />

    <div class="flex flex-col gap-1">
      <BasePasswordInput
        id="password"
        v-model="values.password"
        :error-message="errors.password"
        autocomplete="current-password"
        label="Password"
        name="password"
        required
        validation-rules="required"
      />

      <div class="flex justify-end">
        <a
          :href="authLinks.FORGOT_PASSWORD"
          class="wc-focus body-sm rounded-sm underline hover:no-underline focus-visible:outline-offset-1"
          v-text="'Forgot Password?'"
        />
      </div>
    </div>

    <BaseButton
      :loading="isSubmitting"
      :disabled="isSubmitting"
      class="btn--primary"
      data-test-id="login-submit-button"
      text="Log In"
      type="submit"
    />
  </form>

  <a
    :href="registerLink"
    class="btn btn--outline w-full"
    v-text="'New User? Sign Up'"
  />
</template>

<script lang="ts" setup>
import { useForm } from 'vee-validate'
import { computed, onBeforeMount, ref } from 'vue'

import BaseButton from '@js/components/global/BaseButton.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BasePasswordInput from '@js/components/global/forms/inputs/BasePasswordInput.vue'

import { log } from '@js/utils/logger'
import useAuth from '@js/composables/useAuth'
import { accountLinks, authLinks } from '@js/utils/links'
import useCourseRegistration from '@js/composables/useCourseRegistration'
import { getReturnPath, withReturnPath } from '@js/utils/returnPath'

defineOptions({
  name: 'AppLoginForm',
})

const props = withDefaults(
  defineProps<{
    title?: string
  }>(),
  {
    title: 'Log In',
  },
)

const { values,
  errors,
  resetForm,
  submitCount,
  isSubmitting,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    email: '',
    password: '',
  },
})

const { logInUser } = useAuth()

const {
  courseSKU,
  courseOption,
  craftEntryId,
  setSkuAndCourseOption,
  wasRedirectedFromCourseRegister,
  handleRedirectFromCourseRegister,
} = useCourseRegistration()

const submissionError = ref('')
const returnPath = ref<string | null>(null)

const registerLink = computed(() => {
  if (wasRedirectedFromCourseRegister.value) {
    return `${authLinks.REGISTER}?sku=${courseSKU.value}&course_option=${courseOption.value}&craft_entry_id=${craftEntryId.value}`
  }

  // Carry the return path over so creating an account lands back there too
  if (returnPath.value) {
    return withReturnPath(authLinks.REGISTER, returnPath.value)
  }

  return authLinks.REGISTER
})

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  try {
    submissionError.value = ''

    await logInUser({
      email: values.email,
      password: values.password,
    })

    if (wasRedirectedFromCourseRegister.value) {
      await handleRedirectFromCourseRegister()
    } else {
      // Send the user back where they came from, e.g. an external webinar
      // registration page, otherwise fall back to the dashboard
      window.location.assign(returnPath.value ?? accountLinks.DASHBOARD)
    }
  } catch (error) {
    submissionError.value = 'The email and password you entered does not match our records, please try again or reset your password.'

    log({
      message: 'Login Error',
      file: 'src/wcei/js/components/auth/AppLoginForm.vue',
    })
  } finally {
    resetForm()
  }
})

onBeforeMount(() => {
  setSkuAndCourseOption()

  returnPath.value = getReturnPath()
})
</script>
